<template>
  <hypto-popup :title="(vaId ? 'Update' : 'Add') + ' Virtual Account'">
    <template v-slot:body>
      <div class="merge-fields" style="text-align: right">
        <md-field :class="{'input-error': $v.refNum.$error || formErrors.reference_number.length}">
          <label>Reference Number</label>
          <md-input v-model="refNum" @blur="$v.refNum.$touch()" :ref="'RefNum'" />
        </md-field>
        <h6 class="error-message" v-if="$v.refNum.$error">Please enter Reference Number</h6>
        <error-info v-else :errorList="formErrors.reference_number" />

        <md-field>
          <label>UDF1</label>
          <md-input v-model="udf1" />
        </md-field>

        <md-field>
          <label>UDF2</label>
          <md-input v-model="udf2" />
        </md-field>

        <md-field>
          <label>UDF3</label>
          <md-input v-model="udf3" />
        </md-field>

        <div v-if="showSelfVa">
          <div class="radio-btns">
            <h5>Settle To</h5>
            <md-radio v-model="settleTo" :value="'PARENT'">Parent</md-radio>
            <md-radio v-model="settleTo" :value="'SELF'">Self</md-radio>
          </div>

          <div class="note-panel">
            <div class="row">
              <b>Parent</b>
              <p>The funds received in this virtual account will be settled to your master Hypto account.</p>
            </div>

            <div class="row">
              <b>Self</b>
              <p>The funds received in this virtual account will be settled to the self contained sub virtual account</p>
            </div>
          </div>

          <div class="radio-btns">
            <h5>Parent Type</h5>
            <md-radio v-model="parentType" :value="'VIRTUAL_ACCOUNT'">Virtual Account</md-radio>
            <md-radio v-model="parentType" :value="'PARTNER'">Partner</md-radio>
          </div>

          <md-field v-if="parentType === 'VIRTUAL_ACCOUNT'">
            <label>Parent Virtual Account ID</label>
            <md-input v-model="parentVaId" :ref="'ParentVaId'"
                @keypress="allowAmountFormat($event, parentVaId)" />
          </md-field>

          <div class="check-box">
            <md-checkbox v-model="linkUpi" />
            <p>Link UPI</p>
            <i class="fa fa-info-circle">
              <md-tooltip md-direction="top">Checking this box will allow payins via UPI</md-tooltip>
            </i>
          </div>

          <div v-if="linkUpi">
            <md-field>
              <label>Channel</label>
              <div>
                <md-radio v-model="channel" :value="'ONLINE'">Online</md-radio>
                <md-radio v-model="channel" :value="'OFFLINE'">Offline</md-radio>
              </div>
            </md-field>

            <md-field :class="{'input-error': $v.upiName.$error}">
              <label>UPI Name <i>(Max 40 characters)</i></label>
              <md-input v-model="upiName" :ref="'UpiName'"
                  @blur="$v.upiName.$touch()" />
            </md-field>
            <h6 class="error-message" v-if="$v.upiName.$error && !$v.upiName.alphaNumericSpace">Only alphanumeric characters and space are allowed</h6>
            <h6 class="error-message" v-else-if="$v.upiName.$error">Please enter UPI Name</h6>
          </div>
        </div>

        <add-remitters v-if="!vaId" @setFunctions="setFunctions"
                  @watchRemitters="watchRemitters" />
      </div>
    </template>

    <template v-slot:footer>
      <error-info :errorList="formErrors.virtual_account" />
      <button class="primary-btn align-r" @click="triggerAction" :disabled="isDisableButton">
        {{vaId ? 'Update' : 'Add'}}
      </button>
    </template>
  </hypto-popup>
</template>

<script>
import HyptoPopup from '@/components/global/HyptoPopup'
import ErrorInfo from '@/components/global/ErrorInfo'
import AddRemitters from '@/components/VirtualAccounts/WhitelistRemitters/Add'

import ApiServices from '@/services/api/virtualAccounts'
import formMixins from '@/mixins/formMixins'
import { required } from 'vuelidate/lib/validators'
import { alphaNumericSpace } from '@/utils/helpers/validators'
import { checkFormErrors, openPopup, allowAmountFormat } from '@/utils/helpers'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'VAForm',
  components: { HyptoPopup, ErrorInfo, AddRemitters },
  mixins: [formMixins],
  props: {
    selfVaEnabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      refNum: null,
      udf1: null,
      udf2: null,
      udf3: null,
      remitters: null,
      vaId: false,
      formErrors: {
        reference_number: [],
        virtual_account: []
      },
      settleTo: 'PARENT',
      parentType: 'PARTNER',
      parentVaId: null,
      linkUpi: false,
      upiName: null,
      channel: 'ONLINE'
    }
  },
  validations () {
    let valid = {
      refNum: {
        required
      }
    }

    if (this.linkUpi) {
      valid['upiName'] = {
        required,
        alphaNumericSpace
      }
    }

    return valid
  },
  watch: {
    refNum () {
      this.formErrors.reference_number = []
      this.formErrors.virtual_account = []
    },
    parentType () {
      if (this.parentType === 'VIRTUAL_ACCOUNT') {
        setTimeout(() => {
          this.$refs.ParentVaId.$el.focus()
        })
      }
    },
    linkUpi () {
      if (this.linkUpi) {
        if (!this.linkUpi) {
          this.upiName = null
        }

        setTimeout(() => {
          this.$refs.UpiName.$el.focus()
        })
      }
    },
    parentVaId () {
      this.formErrors.virtual_account = []
    },
    upiName () {
      this.formErrors.virtual_account = []
    }
  },
  computed: {
    ...mapGetters(['getterSectionDetails']),
    showSelfVa () {
      return !this.vaId && this.selfVaEnabled
    }
  },
  mounted () {
    this.$refs.RefNum.$el.focus()
  },
  created () {
    this.vaId = this.$route.params.id || false
    if (this.vaId) {
      this.populateData(this.getterSectionDetails.va)
    }
  },
  methods: {
    checkFormErrors,
    openPopup,
    allowAmountFormat,
    ...mapMutations(['SET_SECTION_DETAILS']),
    populateData (details) {
      const setVal = (key, val) => {
        val = details[val]
        this[key] = val === '-' ? null : val
      }
      setVal('refNum', 'reference_number')
      setVal('udf1', 'udf1')
      setVal('udf2', 'udf2')
      setVal('udf3', 'udf3')
    },
    setFunctions (fun) {
      this.remitters = fun()
    },
    watchRemitters () {
      this.formErrors.virtual_account = []
    },
    getParams () {
      let params = {
        reference_number: this.refNum
      }

      if (!this.vaId) {
        params['whitelisted_remitters'] = this.remitters.get()

        if (this.selfVaEnabled) {
          params['settle_to'] = this.settleTo
          params['parent_type'] = this.parentType
          params['parent_id'] = this.parentVaId
          params['link_upi'] = this.linkUpi
          params['upi_name'] = this.upiName
          params['upi_channel'] = this.channel
        }
      }

      const keys = ['udf1', 'udf2', 'udf3']
      keys.forEach(key => {
        if (this[key] !== null) {
          params[key] = this[key]
        }
      })
      return params
    },
    triggerAction () {
      this.$v.$touch()
      if (!this.$v.$error && (this.vaId || this.remitters.status()) && !this.checkFormErrors(this.formErrors)) {
        const params = this.getParams()

        ApiServices[this.vaId ? 'update' : 'create'](params, this.vaId).then((response) => {
          if (response.success) {
            if (this.vaId) {
              this.SET_SECTION_DETAILS({
                ...this.getterSectionDetails,
                va: response.data.virtual_account
              })
              this.openPopup(null)
            } else {
              this.$emit('refreshList')
            }
            this.successToastMsg(response.message)
          } else {
            this.errorToastMsg(response.message)
          }
          this.enableButton()
        }).catch(error => {
          this.formErrors = {
            ...this.formErrors,
            ...error.body.reason
          }
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/mixins.scss';

  label {
    i {
      color: $grey-text;
      font-weight: 400;
    }
  }

  .note-panel {
    padding: 15px;
    background: $primary-light;
    border-radius: 10px;
    text-align: left;

    .row {
      display: flex;
      align-items: flex-start;
      margin: 8px 0;

      b {
        min-width: 70px;
      }
    }
  }

  .check-box {
    @include custom-flex('');

    p {
      @include normal-text($secondary, 14px, 500);
      margin-right: 10px;
    }
    .fa-info-circle {
      cursor: pointer;
      color: $primary;
    }
  }
</style>
